<template>
	<v-dialog :value="value" max-width="560px" @input="$emit('input', $event)">
		<v-card class="p-8">
			<h6 class="text-xl font-bold mb-4">
				<v-icon class="mr-1">{{ calendar.icon }}</v-icon>
				{{ event ? "Programare " + calendar.type : "Programeaza " + calendar.type }}
			</h6>

			<v-form ref="form" :disabled="readOnly">
				<v-autocomplete v-model="form.work_id" :items="workOptions" item-value="id_work" :item-text="workText"
					label="Lucrare" :disabled="!!event || !!fixedWork" :rules="[required]" outlined dense>
					<template v-slot:item="{ item }">
						<div class="flex flex-col py-1">
							<span class="font-bold">{{ item.client_nume }}</span>
							<span class="text-sm text-gray-600">Contract {{ item.contract_id }} &middot;
								{{ item.client_oras }}</span>
						</div>
					</template>
				</v-autocomplete>

				<!-- Measurements: one day -->
				<v-text-field v-if="calendar.mode === 'day'" v-model="form.startDate" type="date" label="Data"
					:rules="[required]" outlined dense></v-text-field>

				<!-- Production: start + working hours -->
				<template v-else-if="calendar.mode === 'hours'">
					<div class="grid grid-cols-3 gap-x-4">
						<v-text-field v-model="form.startDate" type="date" label="Data inceput" :rules="[required]"
							outlined dense></v-text-field>
						<v-text-field v-model="form.startTime" type="time" step="1800" label="Ora inceput"
							:rules="[required, inWorkingHours]" outlined dense></v-text-field>
						<v-text-field v-model.number="form.hours" type="number" min="0.5" step="0.5" label="Ore"
							:rules="[required, positive]" outlined dense></v-text-field>
					</div>
					<p v-if="endPreview" class="text-sm -mt-2 mb-4">
						Program {{ schedule.startTime.substr(0, 5) }} - {{ schedule.endTime.substr(0, 5) }}
						({{ formatHours(schedule.dayHours) }}/zi). Se termina
						<b>{{ endPreview }}</b>.
					</p>
				</template>

				<!-- Montage: whole days -->
				<div v-else class="grid grid-cols-2 gap-x-4">
					<v-text-field v-model="form.startDate" type="date" label="De la" :rules="[required]" outlined
						dense></v-text-field>
					<v-text-field v-model="form.endDate" type="date" label="Pana la (inclusiv)"
						:rules="[required, endAfterStart]" outlined dense></v-text-field>
				</div>

				<v-autocomplete v-model="form.assignees" :items="peopleOptions" item-value="id" item-text="name"
					:label="calendar.peopleLabel" :loading="loadingBusy" :rules="peopleRules" multiple chips
					small-chips deletable-chips outlined dense>
					<template v-slot:selection="{ item }">
						<v-chip small :color="item.busy && calendar.exclusive ? 'red lighten-4' : undefined">
							<span v-if="item.color" class="dot mr-1" :class="item.color"></span>
							{{ item.name }}
						</v-chip>
					</template>
					<template v-slot:item="{ item }">
						<div class="flex flex-col py-1">
							<span class="inline-flex items-center">
								<span v-if="item.color" class="dot mr-2" :class="item.color"></span>{{ item.name }}
							</span>
							<span v-if="item.busyText && calendar.exclusive" class="text-xs red--text">Ocupat: {{ item.busyText }}</span>
							<span v-else-if="item.busyText" class="text-xs text-gray-600">Programat si la: {{ item.busyText }}</span>
						</div>
					</template>
				</v-autocomplete>

				<v-textarea v-model="form.notes" label="Observatii" rows="2" auto-grow outlined dense></v-textarea>

				<v-alert v-if="deadlineWarning" type="warning" text dense>{{ deadlineWarning }}</v-alert>
				<v-alert v-if="finisajeWarning" type="warning" text dense>
					Finisajele clientului nu sunt confirmate: masuratorile finale nu se pot lua pana nu sunt gata.
				</v-alert>
			</v-form>

			<div class="flex flex-row flex-wrap gap-2">
				<v-btn v-if="!readOnly" color="teal" class="uppercase" dark :loading="saving" @click="save">
					Salveaza
				</v-btn>
				<v-btn v-if="event && !readOnly" color="red" class="uppercase" dark :loading="deleting"
					@click="remove">
					Sterge
				</v-btn>
				<v-btn v-if="event && !fixedWork && canOpenWork" color="blue-grey" class="uppercase" dark :to="'/lucrari/' + form.work_id">
					Vezi lucrarea
				</v-btn>
				<v-spacer />
				<v-btn color="blue-grey" class="uppercase" text @click="$emit('input', false)">Inchide</v-btn>
			</div>
		</v-card>
	</v-dialog>
</template>

<script>
import moment from "moment";
import { CALENDARS, workSchedule, formatHours, finisajeDone } from "@/utils/works";

// Converts between the API's event and the form:
//  - day:   start date only (the API stores the day)
//  - hours: start date + time and the number of working hours (the API works out the end)
//  - range: inclusive day range in the form, exclusive end for the API
export default {
	name: "WorkEventDialog",
	props: {
		value: { type: Boolean, default: false },
		type: { type: String, required: true },
		// The event being edited, as returned by the API; null to create.
		event: { type: Object, default: null },
		// Works that can be picked (the API still checks the stage).
		works: { type: Array, default: () => [] },
		// Preselect and lock the work (from the work's own page).
		fixedWork: { type: Object, default: null },
		// Prefill from a calendar selection: { start: Date, end: Date, allDay }
		selection: { type: Object, default: null },
		readOnly: { type: Boolean, default: false },
		// false for an agent looking at another agent's booking: the work's page is not theirs
		canOpenWork: { type: Boolean, default: true },
	},
	data() {
		return {
			form: this.emptyForm(),
			saving: false,
			deleting: false,
			// people already booked elsewhere in the chosen timeframe: { [personId]: [bookings] }
			busy: {},
			loadingBusy: false,
			busyTimer: null,
			// set while reset() fills the form, so the watchers leave it as loaded
			resetting: false,
		};
	},
	computed: {
		calendar() {
			return CALENDARS[this.type];
		},
		user() {
			return this.$store.getters.loggedUser;
		},
		schedule() {
			return workSchedule(this.$store.state.works.settings);
		},
		workOptions() {
			const list = this.fixedWork ? [this.fixedWork] : [...this.works];
			// The work of an event being edited may no longer be in the bookable list.
			if (this.event && !list.find((w) => w.id_work == this.event.work_id)) {
				list.push({
					id_work: this.event.work_id,
					client_nume: this.event.client_nume,
					client_oras: this.event.client_oras,
					contract_id: this.event.contract_id,
				});
			}
			return list;
		},
		selectedWork() {
			return this.workOptions.find((w) => w.id_work == this.form.work_id) || null;
		},
		peopleOptions() {
			if (this.calendar.people === "agents") {
				return this.$store.state.works.agents.map((u) => ({ id: Number(u.id_user), name: u.username }));
			}
			return this.$store.state.works.people[this.calendar.people].map((p) => {
				const id = Number(p.id_persoana);
				const bookings = this.busy[id] || [];
				return {
					id,
					name: p.name,
					color: p.color,
					busy: bookings.length > 0,
					// On production, a person on an overlapping booking can't be picked. One already
					// picked stays enabled (flagged red, failing validation) so it can still be removed.
					disabled: this.calendar.exclusive && bookings.length > 0 && !this.form.assignees.includes(id),
					busyText: bookings.map((b) => `${b.client_nume || "alta lucrare"} (${this.when(b)})`).join("; "),
				};
			});
		},
		// Every booking needs at least one person: the agent who measures, or the team.
		peopleRules() {
			return [this.required, this.notBusy];
		},
		// What the API needs to work out the timeframe, or null while the form is incomplete.
		busyQuery() {
			if (this.calendar.people === "agents" || !this.value) return null;
			const f = this.form;
			const exclude = this.event ? this.event.id_event : undefined;
			if (this.calendar.mode === "hours") {
				if (!this.startMoment || !(f.hours > 0) || this.inWorkingHours() !== true) return null;
				return { type: this.type, start: `${f.startDate} ${f.startTime}:00`, hours: f.hours, exclude };
			}
			if (!f.startDate || !f.endDate || f.endDate < f.startDate) return null;
			return { type: this.type, ...this.payloadDates(), exclude };
		},
		startMoment() {
			const f = this.form;
			if (!f.startDate || !f.startTime) return null;
			const m = moment(`${f.startDate} ${f.startTime}`, "YYYY-MM-DD HH:mm");
			return m.isValid() ? m : null;
		},
		endPreview() {
			if (!this.startMoment || !(this.form.hours > 0)) return null;
			const end = moment(this.schedule.addHours(this.startMoment.toDate(), this.form.hours));
			const days = ["duminica", "luni", "marti", "miercuri", "joi", "vineri", "sambata"];
			return `${days[end.day()]} ${end.format("DD.MM, HH:mm")}`;
		},
		// Booking a measurement is allowed, but the final measurements can't be taken yet.
		finisajeWarning() {
			if (this.type !== "masuratori") return false;
			const work = this.selectedWork && this.selectedWork.finisaje_finalizate !== undefined ? this.selectedWork : this.event;
			return !!work && work.finisaje_finalizate !== undefined && !finisajeDone(work);
		},
		deadlineWarning() {
			const deadline = (this.selectedWork && this.selectedWork.data_montaj_maxima) || (this.event && this.event.data_montaj_maxima);
			if (this.type !== "montaj" || !deadline) return null;
			const last = this.form.endDate || this.form.startDate;
			if (last && last > deadline) {
				return `Montajul se termina dupa data montaj maxima (${moment(deadline).format("DD.MM.YYYY")}).`;
			}
			return null;
		},
	},
	watch: {
		// Montage is usually done in a day: a new start date brings the end along, unless the end
		// was deliberately set to a later day (a longer montage that still ends after the start).
		"form.startDate"(start, previous) {
			if (this.calendar.mode !== "range" || this.resetting || !start) return;
			const end = this.form.endDate;
			if (!end || end < start || end === previous) {
				this.form.endDate = start;
			}
		},
		// Look up who is busy whenever the timeframe changes (debounced while typing).
		busyQuery: {
			handler(query) {
				clearTimeout(this.busyTimer);
				if (!query) {
					this.busy = {};
					return;
				}
				this.busyTimer = setTimeout(() => this.loadBusy(query), 300);
			},
			deep: true,
		},
		// immediate: the work page mounts the dialog already open
		value: {
			handler(open) {
				if (open) {
					this.loadPeople();
					this.reset();
				}
			},
			immediate: true,
		},
	},
	methods: {
		formatHours,
		async loadBusy(query) {
			this.loadingBusy = true;
			const rows = await this.$store.dispatch("works/fetchBusyPeople", query);
			this.loadingBusy = false;
			// a later change may have replaced this query while it was loading
			if (JSON.stringify(query) !== JSON.stringify(this.busyQuery)) return;
			const busy = {};
			rows.forEach((r) => {
				const id = Number(r.assignee_id);
				(busy[id] = busy[id] || []).push(r);
			});
			this.busy = busy;
		},
		when(b) {
			const start = moment(b.start);
			const end = moment(b.end);
			if (b.all_day == 1) {
				const last = end.clone().subtract(1, "minute");
				return start.isSame(last, "day") ? start.format("DD.MM") : `${start.format("DD.MM")} - ${last.format("DD.MM")}`;
			}
			return start.isSame(end, "day")
				? `${start.format("DD.MM HH:mm")} - ${end.format("HH:mm")}`
				: `${start.format("DD.MM HH:mm")} - ${end.format("DD.MM HH:mm")}`;
		},
		notBusy(ids) {
			if (!this.calendar.exclusive) return true;
			const taken = (ids || []).filter((id) => (this.busy[id] || []).length);
			if (!taken.length) return true;
			const names = this.peopleOptions.filter((p) => taken.includes(p.id)).map((p) => p.name);
			return `Ocupat in acest interval: ${names.join(", ")}`;
		},
		// On every open: the same dialog may switch between calendar types.
		loadPeople() {
			if (this.calendar.people === "agents") {
				this.$store.dispatch("works/fetchAgents").catch(() => {});
			} else {
				this.$store.dispatch("works/fetchPeople", { tip: this.calendar.people }).catch(() => {});
			}
		},
		emptyForm() {
			return {
				work_id: null,
				startDate: "",
				startTime: "",
				endDate: "",
				hours: null,
				assignees: [],
				notes: "",
			};
		},
		async reset() {
			this.resetting = true;
			const form = this.emptyForm();
			if (this.fixedWork) form.work_id = this.fixedWork.id_work;
			this.busy = {};

			// the working day is needed to place production bookings
			if (this.calendar.mode === "hours") {
				await this.$store.dispatch("works/fetchSettings").catch(() => {});
			}
			const schedule = this.schedule;
			const dayStart = schedule.startTime.substr(0, 5);

			if (this.event) {
				const start = moment(this.event.start);
				form.work_id = this.event.work_id;
				form.startDate = start.format("YYYY-MM-DD");
				form.startTime = start.format("HH:mm");
				form.endDate = moment(this.event.end).subtract(1, "minute").format("YYYY-MM-DD");
				form.hours = this.event.hours !== null && this.event.hours !== undefined ? Number(this.event.hours) : null;
				form.assignees = (this.event.assignees || []).map((a) => Number(a.id));
				form.notes = this.event.notes || "";
			} else if (this.selection) {
				const start = moment(this.selection.start);
				const end = moment(this.selection.end);
				form.startDate = start.format("YYYY-MM-DD");
				form.endDate = end.clone().subtract(1, "minute").format("YYYY-MM-DD");
				if (this.calendar.mode === "hours") {
					if (this.selection.allDay) {
						// picked days in the month view: start at opening
						form.startTime = dayStart;
					} else {
						// picked a time span in the week view: that span is the duration
						form.startTime = start.format("HH:mm");
						form.hours = Math.max(0.5, Math.round(end.diff(start, "minutes") / 30) / 2);
					}
				}
			} else if (this.calendar.mode === "hours") {
				// the next working half hour
				const now = moment();
				const rounded = now.clone().startOf("hour").add(now.minutes() === 0 ? 0 : now.minutes() <= 30 ? 30 : 60, "minutes");
				const start = moment(schedule.nextWorkingMoment(rounded.toDate()));
				form.startDate = start.format("YYYY-MM-DD");
				form.startTime = start.format("HH:mm");
			} else {
				form.startDate = moment().format("YYYY-MM-DD");
				form.endDate = form.startDate;
			}

			// The agent booking a measurement usually goes themselves.
			// Only when they are a fence agent, i.e. in the list.
			if (!this.event && this.calendar.people === "agents" && this.user.id_user) {
				await this.$store.dispatch("works/fetchAgents").catch(() => {});
				if (this.$store.state.works.agents.some((a) => a.id_user == this.user.id_user)) {
					form.assignees = [Number(this.user.id_user)];
				}
			}
			this.form = form;
			// after the form watchers have run for the new values
			this.$nextTick(() => {
				this.resetting = false;
			});
			this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation());
		},
		required(v) {
			return (v !== null && v !== "" && v !== undefined && (!Array.isArray(v) || v.length > 0)) || "Obligatoriu";
		},
		positive(v) {
			return Number(v) > 0 || "Mai mare decat 0";
		},
		inWorkingHours() {
			if (!this.startMoment) return true;
			return this.schedule.isWorkingTime(this.startMoment.toDate()) || "In afara programului de lucru";
		},
		endAfterStart() {
			return !this.form.endDate || this.form.endDate >= this.form.startDate || "Sfarsitul trebuie sa fie dupa inceput";
		},
		payloadDates() {
			const f = this.form;
			if (this.calendar.mode === "day") {
				return { start: f.startDate };
			}
			if (this.calendar.mode === "hours") {
				return { start: `${f.startDate} ${f.startTime}:00`, hours: f.hours };
			}
			return {
				start: f.startDate,
				end: moment(f.endDate || f.startDate).add(1, "day").format("YYYY-MM-DD"),
			};
		},
		async save() {
			if (!this.$refs.form.validate()) return;
			const payload = {
				...this.payloadDates(),
				assignees: this.form.assignees,
				notes: this.form.notes,
			};
			this.saving = true;
			try {
				if (this.event) {
					await this.$store.dispatch("works/updateEvent", { id: this.event.id_event, ...payload });
				} else {
					await this.$store.dispatch("works/createEvent", {
						...payload,
						type: this.type,
						work_id: this.form.work_id,
						user_id: this.user.id_user,
					});
				}
				this.$emit("saved");
				this.$emit("input", false);
			} catch (e) {
				// toast already shown by the store
			} finally {
				this.saving = false;
			}
		},
		async remove() {
			if (!confirm("Stergi programarea?")) return;
			this.deleting = true;
			try {
				await this.$store.dispatch("works/deleteEvent", this.event.id_event);
				this.$emit("saved");
				this.$emit("input", false);
			} catch (e) {
				// toast already shown by the store
			} finally {
				this.deleting = false;
			}
		},
		workText(w) {
			return `${w.client_nume} (contract ${w.contract_id})`;
		},
	},
	beforeDestroy() {
		clearTimeout(this.busyTimer);
	},
};
</script>

<style scoped>
.dot {
	border-radius: 100px;
	width: 10px;
	height: 10px;
	display: inline-block;
}
</style>
